/**
 * Substitutes `${recipe:<id>}`, `${recipe:<id>:<sub>}`, `${recipe:<id>:bin}`,
 * and `${recipe:<id>:lib}` markers with absolute /nix/store paths.
 *
 * Matching rules:
 *   ${recipe:excel}          -> /nix/store/.../bin
 *   ${recipe:excel:bin}      -> /nix/store/.../bin
 *   ${recipe:excel:lib}      -> /nix/store/.../lib
 *   ${recipe:excel:share}    -> /nix/store/.../share  (any sub path)
 *
 * The bare ${recipe:<id>} form is treated as `${recipe:<id>:bin}` for
 * ergonomic command-line use; explicit subpaths win.
 */
import { existsSync } from "node:fs";

// Case-sensitive by design: markers must be lowercase to match the declaration
// id map keys. Uppercase markers survive as literal text and are caught by the
// unresolved-marker scan in resources.ts, which skips the declaration cleanly.
const RECIPE_MARKER = /\$\{recipe:([a-z0-9_-]+)(?::([a-z0-9_/.-]+))?\}/g;

export function substituteRecipeTemplating(
  value: string,
  recipeOutPaths: ReadonlyMap<string, string>,
): string {
  return value.replace(RECIPE_MARKER, (_match, id: string, sub: string | undefined) => {
    const outPath = recipeOutPaths.get(id);
    if (!outPath) {
      // Leave the marker in place so the caller can decide policy. The
      // wiring layer surfaces unresolved markers as a structured error
      // before subprocess spawn.
      return _match;
    }
    if (!sub) return `${outPath}/bin`;
    return `${outPath}/${sub}`;
  });
}

export function substituteRecipeMap(
  obj: Record<string, string> | undefined,
  recipeOutPaths: ReadonlyMap<string, string>,
): Record<string, string> | undefined {
  if (!obj) return obj;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj)) {
    out[k] = substituteRecipeTemplating(v, recipeOutPaths);
  }
  return out;
}

// `${workspace}` resolves to the session workspace root (the dir holding
// `.skaile/`). Applied to ALL MCP declarations (recipe and command-shape alike),
// unlike `${recipe:…}` which only applies to recipe-launcher decls.
const WORKSPACE_MARKER = /\$\{workspace\}/g;

export function substituteWorkspaceToken(value: string, workspaceDir: string): string {
  return value.replace(WORKSPACE_MARKER, workspaceDir);
}

export function substituteWorkspaceTokenMap(
  obj: Record<string, string> | undefined,
  workspaceDir: string,
): Record<string, string> | undefined {
  if (!obj) return obj;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj)) {
    out[k] = substituteWorkspaceToken(v, workspaceDir);
  }
  return out;
}

export interface MissingSubstitutedPath {
  field: string;
  path: string;
  error: string;
}

/**
 * Checks that the resolved binary path (command and args[0]) produced by
 * recipe substitution actually exists on the filesystem.
 *
 * Only checks `command` and `args[0]` — these are the fields where ENOENT
 * is most user-visible (subprocess spawn fails immediately). Other args and
 * env values are not checked to keep this focused.
 *
 * Returns an array of missing-path entries; an empty array means all checks
 * passed. The caller in resources.ts skips the declaration when non-empty.
 */
export function validateSubstitutedPaths(
  command: string | undefined,
  args: string[] | undefined,
): MissingSubstitutedPath[] {
  const missing: MissingSubstitutedPath[] = [];

  if (command?.startsWith("/nix/store/") && !existsSync(command)) {
    missing.push({
      field: "command",
      path: command,
      error: `resolved path does not exist: ${command}`,
    });
  }

  const firstArg = args?.[0];
  if (firstArg?.startsWith("/nix/store/") && !existsSync(firstArg)) {
    missing.push({
      field: "args[0]",
      path: firstArg,
      error: `resolved path does not exist: ${firstArg}`,
    });
  }

  return missing;
}
